import { Avatar, AvatarGroup, Button } from "@mui/material";
import { useState } from "react";
import { plural } from "../access-model";
import { useProgramAccess } from "../hooks/use-program-access";
import { AccessDialog, listedContributors } from "./access-dialog";

import type { ProgramNode, ProgramTree } from "../../program-tree/program-tree";
import type { FC } from "react";

/** Opens the Access dialog of a program, and says how many contributors can see it. */
export const AccessButton: FC<{ tree: ProgramTree; program: ProgramNode }> = ({
  tree,
  program,
}) => {
  const [open, setOpen] = useState(false);
  const { data: loaded, reload } = useProgramAccess();
  // A reload clears the data until it loads again. Keep the last data, so that the dialog stays
  // mounted with its state, such as the message of the last change.
  const [data, setData] = useState(loaded);
  if (loaded && loaded !== data) setData(loaded);

  if (!data) return null;

  const listed = listedContributors(data, tree, program);
  const text = listed.length ? plural(listed.length, "contributor") : "Admins only";

  return (
    <>
      <Button
        variant="outlined"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={`Access: ${listed.length ? text : "admins only"}`}
        startIcon={
          listed.length ? (
            <AvatarGroup
              max={3}
              sx={{ "& .MuiAvatar-root": { width: 24, height: 24, fontSize: 11 } }}
            >
              {listed.map(({ person }) => (
                <Avatar key={person.id}>{person.initials}</Avatar>
              ))}
            </AvatarGroup>
          ) : undefined
        }
        data-analytics-id="program-access-open"
      >
        {text}
      </Button>
      <AccessDialog
        open={open}
        onClose={() => setOpen(false)}
        tree={tree}
        program={program}
        data={data}
        reload={reload}
      />
    </>
  );
};
